// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the measured error budget a release descriptor carries
 * (`release.json` → `errorBudget`, `tolerance`, `exact`;
 * docs/AYNI_DATA_INTAKE_API.md §10.1; owner decision O9, 2026-10-06:
 * every release descriptor carries it, public streams too). Private releases carry it now; the stream host's public releases
 * will once it adds the fields. Read here (pure) and put in words by
 * `services/errorBudget.ts`; kept
 * in `types/` so `ReleaseEncoding` can name it.
 *
 * Every number is in the variable's units unless its name says share
 * (a fraction 0–1). A field the descriptor leaves out is null, never a
 * guess.
 */

export interface ErrorBudget {
  readonly version: string | null
  readonly units: string | null
  /** 8-bit rounding: how many codes carry data, one code's width and half of it. */
  readonly quantisation: { readonly codes: number | null; readonly step: number | null; readonly halfStep: number | null } | null
  /** The video codec, decoded back against what went into the encoder. */
  readonly codec: {
    readonly mean: number | null
    readonly p99: number | null
    readonly max: number | null
    readonly worstFrame: { readonly index: number | null; readonly time: string | null } | null
  } | null
  /** Cells outside the chosen range: shares clipped low and high, and the furthest value. */
  readonly clipping: { readonly lowShare: number | null; readonly highShare: number | null; readonly furthest: number | null } | null
  /** Regridding: the method, both resolutions, and the difference read back at source points. */
  readonly regrid: {
    readonly method: string | null
    readonly sourceResolution: string | null
    readonly targetResolution: string | null
    readonly mean: number | null
    readonly p99: number | null
  } | null
  /** Frames against the source's time axis. */
  readonly time: { readonly missing: number | null; readonly repeated: number | null; readonly frozen: number | null } | null
  /** Cells without a value, per frame. */
  readonly noData: { readonly meanShare: number | null; readonly maxShare: number | null } | null
}

/** The customer's tolerance and what the release achieved against it (`release.json` → `tolerance`). */
export interface ToleranceResult {
  /** "`quantile` of absolute errors within `abs`": 0.99 and 0.1 read "99 % within 0.1". */
  readonly quantile: number
  readonly abs: number
  readonly achieved: number | null
  readonly pass: boolean | null
  /** The encoding the pipeline chose to meet it: lossy, lossless, or exact values only. */
  readonly encoding: string | null
}

/** Everything a descriptor says about how far its values can be trusted. */
export interface ReleaseAccuracy {
  readonly budget: ErrorBudget
  readonly tolerance: ToleranceResult | null
  /** Whether the release has exact values (Zarr) beside the video. */
  readonly exact: boolean
}

// ---------------------------------------------------------------------------
// Reading (pure, so `services/dashRelease.ts` can attach it to an encoding)
// ---------------------------------------------------------------------------

const rec = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 200) : null)

/** An `errorBudget` object as the app reads it, or null when it is not an object. Unknown fields are dropped. */
export function parseErrorBudget(raw: unknown): ErrorBudget | null {
  const b = rec(raw)
  if (!b) return null
  const q = rec(b.quantisation)
  const c = rec(b.codec)
  const worst = rec(c?.worstFrame)
  const clip = rec(b.clipping)
  const rg = rec(b.regrid)
  const tm = rec(b.time)
  const nd = rec(b.noData)
  return {
    version: str(b.version),
    units: str(b.units),
    quantisation: q ? { codes: num(q.codes), step: num(q.step), halfStep: num(q.halfStep) } : null,
    codec: c
      ? { mean: num(c.mean), p99: num(c.p99), max: num(c.max), worstFrame: worst ? { index: num(worst.index), time: str(worst.time) } : null }
      : null,
    clipping: clip ? { lowShare: num(clip.lowShare), highShare: num(clip.highShare), furthest: num(clip.furthest) } : null,
    regrid: rg
      ? { method: str(rg.method), sourceResolution: str(rg.sourceResolution), targetResolution: str(rg.targetResolution), mean: num(rg.mean), p99: num(rg.p99) }
      : null,
    time: tm ? { missing: num(tm.missing), repeated: num(tm.repeated), frozen: num(tm.frozen) } : null,
    noData: nd ? { meanShare: num(nd.meanShare), maxShare: num(nd.maxShare) } : null,
  }
}

/** A descriptor's `tolerance`, or null when it set none or it is not readable. */
export function parseTolerance(raw: unknown): ToleranceResult | null {
  const tl = rec(raw)
  const quantile = num(tl?.quantile)
  const abs = num(tl?.abs)
  if (!tl || quantile === null || abs === null || !(quantile > 0 && quantile <= 1) || !(abs > 0)) return null
  return { quantile, abs, achieved: num(tl.achieved), pass: typeof tl.pass === 'boolean' ? tl.pass : null, encoding: str(tl.encoding) }
}

/** What a `release.json` says about its accuracy, or null when it carries no error budget. */
export function parseReleaseAccuracy(release: unknown): ReleaseAccuracy | null {
  const r = rec(release)
  const budget = parseErrorBudget(r?.errorBudget)
  if (!r || !budget) return null
  return { budget, tolerance: parseTolerance(r.tolerance), exact: rec(r.exact) !== null }
}
